/* Reviews (PISR ERP — Reviews PRD, PR-2). Three jobs, all enhancements over markup that already
 * works without this file:
 *
 *  1. The badge's `#reviews` link scrolls smoothly, moves keyboard focus onto the section and
 *     highlights the heading for a moment (US-005). Without the script it is a plain jump.
 *  2. "Show more reviews" fetches the next page from the ERP's public page endpoint and appends
 *     cards built to the SAME structure `sections/erp-reviews.liquid` renders (US-006). The
 *     button ships hidden and is revealed here, so a page without the script never shows a
 *     button nothing answers.
 *  3. The write-a-review form posts JSON to the ERP's public submit endpoint and replaces
 *     itself with "Thanks for your review" (US-007). Same rule: the button is revealed here.
 *
 * EVERY PIECE OF REVIEW TEXT IS SET WITH textContent. The cards carry words a stranger typed;
 * innerHTML anywhere in this file would let one of them put script on every product page. The
 * contract test asserts the absence.
 *
 * Wrapped in one IIFE, registered on `shopify:section:load` as well as page load so the theme
 * editor's re-render of the section picks it up again. Each section root is initialised once,
 * marked with `data-erp-ready`. */
(function () {
  "use strict";

  var STAR_PATH = "M12 2.5l2.9 6.2 6.8.8-5 4.7 1.3 6.7L12 17.6l-6 3.3 1.3-6.7-5-4.7 6.8-.8z";
  var TICK_PATH = "M20 6 9 17l-5-5";
  var SVG_NS = "http://www.w3.org/2000/svg";
  var PAGE_PATH = "/api/modules/store-manager/reviews/public/page";
  /* There is deliberately NO page size here. This file used to carry a copy of the server's,
   * because the page number was computed from it and a mismatch would skip or repeat reviews.
   * Asking for what comes after the last card removes the arithmetic and with it the need to
   * agree on a page size at all — the server decides how many to send, and says whether more
   * follow. One less constant that can be right in one file and wrong in another. */
  var SUBMIT_PATH = "/api/modules/store-manager/reviews/public/submit";

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = String(text);
    return node;
  }

  function svgPath(d) {
    var svg = document.createElementNS(SVG_NS, "svg");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("focusable", "false");
    var path = document.createElementNS(SVG_NS, "path");
    path.setAttribute("d", d);
    svg.appendChild(path);
    return svg;
  }

  /* The same five boxes `snippets/erp-review-stars.liquid` draws: full, empty, or part-filled
   * through `--erp-star-pct` on the one box the fraction lands in. */
  function stars(rating, size) {
    var value = Number(rating) || 0;
    var wrap = el("span", "erp-stars");
    wrap.style.setProperty("--erp-star-size", size + "px");
    wrap.setAttribute("role", "img");
    wrap.setAttribute("aria-label", Math.round(value * 10) / 10 + " out of 5 stars");
    for (var i = 1; i <= 5; i += 1) {
      var filled = value - i + 1;
      var pct = filled >= 1 ? 100 : filled <= 0 ? 0 : Math.round(filled * 100);
      var box = el("span", "erp-stars__box");
      box.style.setProperty("--erp-star-pct", pct + "%");
      box.appendChild(svgPath(STAR_PATH));
      wrap.appendChild(box);
    }
    return wrap;
  }

  /* "Sep 7, 2026" — the same shape Liquid's `date: '%b %-d, %Y'` renders on the first page, AND
   * THE SAME DAY (SMR-059). Liquid dates in the shop's timezone. This file used to date in the
   * viewer's, so a review stamped in the shop's evening was one day above the fold and the next
   * below it for a shopper in Europe. The section now states the shop's UTC offset as of render
   * (`data-erp-utc-offset`, e.g. "-0400"), and every appended card is dated at that offset.
   *
   * Two paths, one result. When the viewer's own clock keeps the same offset right now, the
   * browser dates the instant in its own zone — almost certainly the shop's zone or one on the
   * same clock — and daylight saving is then exact for every instant, as it was before for
   * viewers near the shop. Otherwise the instant is shifted by the offset and dated from its
   * parts. THE LIMIT of that second path: the offset is the shop's NOW, so where the shop
   * observes daylight saving a review from the other half of the year is shifted by an hour too
   * many or too few — which changes its date only when it was stamped within an hour of the
   * shop's midnight. Accepted: the ERP does not hold the shop's zone, Liquid cannot name it, and
   * a page a CDN served across the switch carries the older offset for the cache's life anyway.
   * An older copy of the section states no offset; then the viewer's zone is used, as before. */
  var MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  /* "-0400" or "+05:30" → minutes east of UTC, or null for anything else. */
  function parseUtcOffset(text) {
    var m = /^([+-])(\d{2}):?(\d{2})$/.exec(String(text || "").trim());
    if (!m) return null;
    var minutes = Number(m[2]) * 60 + Number(m[3]);
    return m[1] === "-" ? -minutes : minutes;
  }

  /* The calendar day of `iso` at the shop's offset: { y, m (0-11), d }, or null when unreadable. */
  function shopDay(iso, offsetMinutes) {
    var date = new Date(iso);
    if (isNaN(date.getTime())) return null;
    // `getTimezoneOffset` is minutes WEST of UTC, so the sign flips. Compared at NOW, not at the
    // review's instant: the question is whether this clock is the shop's clock today.
    if (offsetMinutes === null || -new Date().getTimezoneOffset() === offsetMinutes) {
      return { y: date.getFullYear(), m: date.getMonth(), d: date.getDate() };
    }
    var shifted = new Date(date.getTime() + offsetMinutes * 60000);
    return { y: shifted.getUTCFullYear(), m: shifted.getUTCMonth(), d: shifted.getUTCDate() };
  }

  function formatDate(iso, offsetMinutes) {
    var day = shopDay(iso, offsetMinutes);
    return day ? MONTHS[day.m] + " " + day.d + ", " + day.y : "";
  }

  /* "2026-09-07" for the <time> element's machine-readable attribute, same day as the text. */
  function isoDay(iso, offsetMinutes) {
    var day = shopDay(iso, offsetMinutes);
    if (!day) return "";
    var mm = day.m + 1;
    return day.y + "-" + (mm < 10 ? "0" : "") + mm + "-" + (day.d < 10 ? "0" : "") + day.d;
  }

  function paragraphWithBreaks(className, text) {
    // Multi-line review text: each newline becomes a <br>, the words stay text.
    var p = el("p", className);
    var lines = String(text || "").split(/\r?\n/);
    for (var i = 0; i < lines.length; i += 1) {
      if (i > 0) p.appendChild(document.createElement("br"));
      p.appendChild(document.createTextNode(lines[i]));
    }
    return p;
  }

  function card(review, storeName, utcOffset) {
    var li = el("li", "erp-review-card");
    if (review.id) li.setAttribute("data-erp-review-id", String(review.id));

    var meta = el("div", "erp-review-card__meta");
    meta.appendChild(stars(review.rating, 16));
    var time = el("time", "erp-review-card__date", formatDate(review.reviewed_at, utcOffset));
    var day = isoDay(review.reviewed_at, utcOffset);
    if (day) time.setAttribute("datetime", day);
    meta.appendChild(time);
    li.appendChild(meta);

    if (review.title) li.appendChild(el("p", "erp-review-card__title", review.title));
    li.appendChild(paragraphWithBreaks("erp-review-card__body", review.body));

    var author = el("div", "erp-review-card__author");
    author.appendChild(el("span", "erp-review-card__name", review.author));
    if (review.verified === true) {
      var verified = el("span", "erp-review-card__verified");
      verified.appendChild(svgPath(TICK_PATH));
      verified.appendChild(document.createTextNode("Verified purchase"));
      author.appendChild(verified);
    }
    li.appendChild(author);

    if (review.product && review.product.handle && review.product.title) {
      var product = el("p", "erp-review-card__product", "Bought: ");
      var link = el("a", null, review.product.title);
      // A relative product URL on THIS store — never a URL the response chose.
      link.setAttribute("href", "/products/" + encodeURIComponent(review.product.handle));
      product.appendChild(link);
      li.appendChild(product);
    }

    if (review.reply) {
      var reply = el("div", "erp-review-card__reply");
      reply.appendChild(el("span", "erp-review-card__reply-label", "Reply from " + storeName));
      reply.appendChild(paragraphWithBreaks(null, review.reply));
      li.appendChild(reply);
    }
    return li;
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function setStatus(node, message, isError) {
    if (!node) return;
    node.textContent = message || "";
    node.classList.toggle("erp-reviews__status--error", Boolean(isError));
  }

  function initSection(root) {
    if (!root || root.getAttribute("data-erp-ready") === "1") return;
    root.setAttribute("data-erp-ready", "1");

    var origin = String(root.getAttribute("data-erp-origin") || "").replace(/\/+$/, "");
    var shop = root.getAttribute("data-erp-shop") || "";
    var total = Number(root.getAttribute("data-erp-total")) || 0;
    var shown = Number(root.getAttribute("data-erp-shown")) || 0;
    // The shop's UTC offset the section was rendered at; null on a page from an older section.
    var utcOffset = parseUtcOffset(root.getAttribute("data-erp-utc-offset"));
    // The section states the store's name outright. The two fallbacks are only for a page rendered
    // by an older copy of the section: a reply label already on the page, then the page title —
    // which is a guess, because Impact appends the shop name to the title only when the title does
    // not already contain it, so a product named after the store yields the whole title.
    var storeName = root.getAttribute("data-erp-store") || "";
    if (!storeName) {
      var replyLabel = root.querySelector(".erp-review-card__reply-label");
      if (replyLabel) storeName = replyLabel.textContent.replace(/^Reply from\s+/, "");
    }
    if (!storeName) storeName = document.title.split(/\s[–—-]\s/).pop() || "the store";

    // ---- 2. show more ----
    var grid = root.querySelector("[data-erp-grid]");
    var more = root.querySelector("[data-erp-more]");
    var shownCount = root.querySelector("[data-erp-shown-count]");
    var totalCount = root.querySelector("[data-erp-total-count]");
    var totalNoun = root.querySelector("[data-erp-total-noun]");

    /* "SHOW MORE" ASKS FOR WHAT COMES AFTER THE LAST CARD, NOT FOR THE NEXT PAGE NUMBER.
     *
     * The cards on the page are drawn by Liquid from the feed record, and a CDN can serve that
     * HTML long after the list has moved. So the browser's count of its own cards is NOT a
     * position in the server's list, and every attempt to make it one produced a defect of its
     * own: a counter starting at one skipped whatever Liquid had dropped; arithmetic over the
     * rendered cards refetched a page it had already asked for and then called that the end.
     *
     * Naming the last card removes the gap instead of measuring it. There is no offset to be
     * stale, none to run off the end of, and — because nothing is advanced when a request is
     * made — a failed press changes nothing, so the next press asks the identical question. The
     * response is still filtered against the ids on screen: cheap, and the one thing that stays
     * true whatever the server did.
     *
     * WHAT THIS DOES NOT RECOVER, stated because the trade is deliberate: a card Liquid dropped
     * from the MIDDLE of the rendered list. The feed's `newest` is a list of references, and a
     * record deleted by hand in the Shopify admin renders nothing, so eleven cards can appear
     * where twelve were counted. Continuing from the last card steps over that hole rather than
     * going back for it — the arithmetic version re-requested the top and happened to catch it.
     * The cost is one review missing until the next drain rebuilds the feed, which is the same
     * pass `requeueDanglingFeedRows` heals it in; the cost of the arithmetic version was every
     * review past a certain point, for the whole session. Bounded and self-healing beats
     * unbounded and sticky, but it is a trade, not a free win.
     *
     * `has_more` comes from the server, which observes it by looking one row further than a page.
     * The end of the list is never inferred here from a page arriving short — that inference is
     * wrong exactly when the remainder is a whole number of pages, and being wrong there hid
     * every review past that point. */
    var seen = Object.create(null);
    function remember(id) {
      if (id) seen[String(id)] = true;
    }
    var rendered = grid ? grid.querySelectorAll("[data-erp-review-id]") : [];
    for (var s = 0; s < rendered.length; s += 1) remember(rendered[s].getAttribute("data-erp-review-id"));

    /* `total` is the store's, and `shown` is this page's, and they are allowed to differ at the
     * end: a cached page begins partway down the list, so its last card can be the last review
     * while some newer ones were never on it. Both numbers stay honest and the button goes away —
     * a reload shows the rest. What must never happen is the opposite, overwriting the store's
     * total with what this page managed to reach. */
    var exhausted = false;
    function refreshFooter() {
      if (shownCount) shownCount.textContent = String(shown);
      if (totalCount) totalCount.textContent = String(total);
      // The noun follows the number it counts, here and in the Liquid that first rendered it.
      if (totalNoun) totalNoun.textContent = total === 1 ? "review" : "reviews";
      if (more) more.hidden = !(origin && shop && !exhausted && shown < total);
    }

    function lastCardId() {
      var cards = grid.querySelectorAll("[data-erp-review-id]");
      var last = cards[cards.length - 1];
      return last ? last.getAttribute("data-erp-review-id") || "" : "";
    }

    if (more && grid) {
      more.addEventListener("click", function () {
        more.disabled = true;
        var after = lastCardId();
        /* THE PAGE-1 FALLBACK IS FOR AN EMPTY GRID, AND ONLY THAT (SMR-061). `lastCardId` answers
         * "" both when there are no cards and when the last card carries a blank id — a live
         * record whose `erp_id` was blanked by hand. Asking for page 1 in the second case
         * returns rows that are all already on screen, appends nothing, and the no-progress stop
         * then hides the button with the rest of the list unreachable. A card we cannot name is
         * a card we cannot continue from, so that case stops here, honestly, and a reload starts
         * from a list we can place. */
        if (!after && grid.querySelectorAll("[data-erp-review-id]").length > 0) {
          exhausted = true;
          more.disabled = false;
          refreshFooter();
          return;
        }
        var url =
          origin +
          PAGE_PATH +
          "?shop=" +
          encodeURIComponent(shop) +
          (after ? "&after=" + encodeURIComponent(after) : "&page=1");
        fetch(url, { method: "GET", mode: "cors", credentials: "omit" })
          .then(function (res) {
            if (!res.ok) {
              /* A REFUSAL THE SERVER WILL REPEAT ENDS THE LIST (SMR-061). A 400 is a request the
               * server calls malformed — an `after` it cannot read — and it will not read it any
               * better next time; a 403 is a shop or origin it does not serve, or reviews switched
               * off since this page was cached. Re-arming the button for those repeats the
               * identical failing request for as long as the shopper keeps pressing. A 429 and
               * every 5xx are the transient ones: the button comes back and the next press asks
               * the same question, which is the right thing for a blip. */
              if (res.status >= 400 && res.status < 500 && res.status !== 429) exhausted = true;
              throw new Error("page " + res.status);
            }
            return res.json();
          })
          .then(function (data) {
            var reviews = Array.isArray(data && data.reviews) ? data.reviews : [];
            var added = 0;
            for (var i = 0; i < reviews.length; i += 1) {
              var id = reviews[i] && reviews[i].id;
              if (id && seen[String(id)]) continue;
              remember(id);
              grid.appendChild(card(reviews[i], storeName, utcOffset));
              shown += 1;
              added += 1;
            }
            if (typeof data.total === "number") total = data.total;
            // The server's answer is the normal way this ends. The second test is a stop for a
            // press that made NO PROGRESS, which under a cursor can only mean the far end has
            // moved out from under us — every row after a named card is one we do not have, so a
            // response that appends nothing is a response we cannot act on. Without it, such a
            // press repeats identically for as long as the shopper keeps pressing. This is NOT
            // the old "nothing new means the end" inference, which ran under offsets where
            // overlap was routine and which also rewrote the store's total; nothing here touches
            // the total.
            if (data.has_more === false || added === 0) exhausted = true;
          })
          .catch(function () {
            // Nothing was consumed by the attempt, so there is nothing to put back — the next
            // press asks the same question from the same card. Unless the answer was a refusal
            // the server will repeat, in which case `exhausted` was set above and the footer
            // takes the button away.
          })
          .then(function () {
            more.disabled = false;
            refreshFooter();
          });
      });
    }
    refreshFooter();

    // ---- 3. write a review ----
    var toggle = root.querySelector("[data-erp-write]");
    var panel = root.querySelector("[data-erp-form-panel]");
    var form = root.querySelector("[data-erp-form]");
    if (toggle && panel && form && origin && shop) {
      toggle.hidden = false;
      toggle.addEventListener("click", function () {
        panel.hidden = !panel.hidden;
        toggle.setAttribute("aria-expanded", panel.hidden ? "false" : "true");
        if (!panel.hidden) {
          var first = form.querySelector("input[name='rating']");
          if (first && typeof first.focus === "function") first.focus();
        }
      });

      form.addEventListener("submit", function (event) {
        event.preventDefault();
        var status = form.querySelector("[data-erp-status]");
        var submit = form.querySelector("button[type='submit']");
        var data = new FormData(form);
        var field = function (name) {
          var value = data.get(name);
          return typeof value === "string" ? value : "";
        };
        var payload = {
          shop: shop,
          rating: field("rating"),
          title: field("title"),
          body: field("body"),
          name: field("name"),
          email: field("email"),
          order_number: field("order_number"),
          website: field("website")
        };
        if (!payload.rating) return setStatus(status, "Please choose a star rating.", true);
        if (!payload.body.trim()) return setStatus(status, "Please write a few words.", true);
        if (!payload.name.trim()) return setStatus(status, "Please add your name.", true);
        if (!payload.email.trim()) return setStatus(status, "Please add your email.", true);

        if (submit) submit.disabled = true;
        setStatus(status, "Sending…", false);
        fetch(origin + SUBMIT_PATH, {
          method: "POST",
          mode: "cors",
          credentials: "omit",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        })
          .then(function (res) {
            if (res.status === 201) {
              var thanks = el("p", "erp-reviews__thanks", "Thanks for your review");
              panel.replaceChildren(thanks);
              toggle.hidden = true;
              thanks.setAttribute("tabindex", "-1");
              thanks.focus();
              return;
            }
            if (res.status === 429) throw new Error("Please try again in a little while.");
            if (res.status === 403) throw new Error("Reviews are closed right now.");
            if (res.status === 400 || res.status === 413) throw new Error("Please check the form and try again.");
            throw new Error("Something went wrong. Please try again.");
          })
          .catch(function (err) {
            setStatus(status, err && err.message ? err.message : "Something went wrong. Please try again.", true);
          })
          .then(function () {
            if (submit) submit.disabled = false;
          });
      });
    }
  }

  // ---- 1. the badge link ----
  document.addEventListener("click", function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== "function") return;
    var badge = target.closest("[data-erp-review-badge]");
    if (!badge) return;
    var section = document.getElementById("reviews");
    if (!section) return; // no section on this page: let the browser do what the href says
    event.preventDefault();
    section.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });
    try {
      section.focus({ preventScroll: true });
    } catch (e) {
      section.focus();
    }
    section.classList.add("erp-is-highlighted");
    window.setTimeout(function () {
      section.classList.remove("erp-is-highlighted");
    }, 2000);
    if (window.history && typeof window.history.replaceState === "function") {
      window.history.replaceState(null, "", "#reviews");
    }
  });

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll("[data-erp-reviews]");
    for (var i = 0; i < roots.length; i += 1) initSection(roots[i]);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }
  document.addEventListener("shopify:section:load", function (event) {
    initAll(event.target || document);
  });
})();
